// src/components/PDF/AddMediaPDF.js (Key fixes for coordinate system)
import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, Plus, Image, Link, StickyNote, Trash2, Save, X, Edit, Scissors } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { getDocument } from 'pdfjs-dist';
import { PDFDocument, rgb, PDFName, PDFString, degrees } from 'pdf-lib';
import interact from 'interactjs';
import PDFPreviewSave from './PDFPreviewSave';
import { useAuthGuard } from '../../hooks/useAuthGuard';

// Keep the same modal components...
const LinkModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({
    text: initialData.text || 'Click Me',
    url: initialData.url || 'https://',
    backgroundColor: initialData.backgroundColor || '#232751',
    color: initialData.color || '#ffffff',
    fontSize: initialData.fontSize || 16,
    hasBackground: initialData.hasBackground ?? true
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        text: initialData.text || 'Click Me',
        url: initialData.url || 'https://',
        backgroundColor: initialData.backgroundColor || '#232751',
        color: initialData.color || '#ffffff',
        fontSize: initialData.fontSize || 16,
        hasBackground: initialData.hasBackground ?? true
      });
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.text.trim() || !formData.url.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    onSave({
      ...formData,
      fontSize: Number(formData.fontSize)
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Link Button</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input
              type="text"
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
              placeholder="Enter button text"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.hasBackground}
                onChange={(e) => {
                  const hasBackground = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    hasBackground,
                    color: !hasBackground && prev.color === '#ffffff' ? '#232751' : prev.color
                  }));
                }}
                className="rounded border-gray-300 text-[#232751] focus:ring-[#232751]"
              />
              Show background
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text Size: {formData.fontSize}px
                </label>
                <input
                  type="range"
                  min="8"
                  max="36"
                  step="1"
                  value={formData.fontSize}
                  onChange={(e) => setFormData(prev => ({ ...prev, fontSize: Number(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>

            {formData.hasBackground && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <input
                  type="color"
                  value={formData.backgroundColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#232751] text-white rounded-lg hover:bg-[#1b1f43] transition-colors"
            >
              Add Link
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NoteModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({
    text: initialData.text || 'Add your note here...',
    backgroundColor: initialData.backgroundColor || '#facc15'
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        text: initialData.text || 'Add your note here...',
        backgroundColor: initialData.backgroundColor || '#facc15'
      });
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.text.trim()) {
      toast.error('Please enter note text');
      return;
    }
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Note</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note Text</label>
            <textarea
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
              rows={4}
              placeholder="Enter your note text"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
            <div className="flex space-x-2">
              {['#facc15', '#fbbf24', '#ffffff', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'].map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, backgroundColor: color }))}
                  className={`w-8 h-8 rounded-full border-2 ${formData.backgroundColor === color ? 'border-gray-800' : 'border-gray-300'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#232751] text-white rounded-lg hover:bg-[#1b1f43] transition-colors"
            >
              Add Note
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TextModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({
    text: initialData.text || 'Add your text here',
    color: initialData.color || '#111827',
    fontSize: initialData.fontSize || 18
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        text: initialData.text || 'Add your text here',
        color: initialData.color || '#111827',
        fontSize: initialData.fontSize || 18
      });
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.text.trim()) {
      toast.error('Please enter text');
      return;
    }
    onSave({
      ...formData,
      fontSize: Number(formData.fontSize)
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Text</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
            <textarea
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
              rows={4}
              placeholder="Enter text"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Text Size: {formData.fontSize}px
              </label>
              <input
                type="range"
                min="8"
                max="48"
                step="1"
                value={formData.fontSize}
                onChange={(e) => setFormData(prev => ({ ...prev, fontSize: Number(e.target.value) }))}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#232751] text-white rounded-lg hover:bg-[#1b1f43] transition-colors"
            >
              Add Text
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddMediaPDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [mediaElements, setMediaElements] = useState({});
  const [selectedElement, setSelectedElement] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [isSnipping, setIsSnipping] = useState(false);
  const workingAreaRef = useRef(null);
  const snipCleanupRef = useRef(null);
  const [showPreviewSave, setShowPreviewSave] = useState(false);
  const [processedBlob, setProcessedBlob] = useState(null);
  // Helper functions
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? rgb(
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255
    ) : rgb(0, 0, 0);
  };

  const wrapText = (text, maxWidth, fontSize) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    const avgCharWidth = fontSize * 0.6;
    const maxCharsPerLine = Math.floor(maxWidth / avgCharWidth);

    for (const word of words) {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    
    return lines.slice(0, 8);
  };

  // PDF.js displays a page after applying its /Rotate value, while pdf-lib
  // draws in the page's original (unrotated) coordinate system. Convert the
  // top-left browser coordinates back to that system before exporting.
  const getExportGeometry = (page, pageData) => {
    // Prefer the exact transform used by PDF.js to render the editor. This is
    // more reliable than reconstructing it from pdf-lib's page boxes because
    // some generators encode a landscape slide as a rotated portrait page.
    if (pageData.viewportTransform) {
      return {
        rotation: ((pageData.rotation % 360) + 360) % 360,
        transform: pageData.viewportTransform,
        unitScale: Math.hypot(pageData.viewportTransform[0], pageData.viewportTransform[1]),
        scaleX: 1,
        scaleY: 1
      };
    }

    const cropBox = page.getCropBox();
    const rotation = ((page.getRotation().angle % 360) + 360) % 360;
    const isQuarterTurn = rotation === 90 || rotation === 270;
    const displayWidth = isQuarterTurn ? cropBox.height : cropBox.width;
    const displayHeight = isQuarterTurn ? cropBox.width : cropBox.height;

    return {
      cropBox,
      rotation,
      unitScale: 1,
      scaleX: displayWidth / pageData.pdfDimensions.width,
      scaleY: displayHeight / pageData.pdfDimensions.height
    };
  };

  const browserPointToPdf = (displayX, displayY, geometry) => {
    const { cropBox, rotation, transform } = geometry;

    if (transform) {
      const [a, b, c, d, e, f] = transform;
      const determinant = (a * d) - (b * c);
      const translatedX = displayX - e;
      const translatedY = displayY - f;

      return {
        x: ((d * translatedX) - (c * translatedY)) / determinant,
        y: ((a * translatedY) - (b * translatedX)) / determinant
      };
    }

    switch (rotation) {
      case 90:
        return { x: cropBox.x + displayY, y: cropBox.y + displayX };
      case 180:
        return {
          x: cropBox.x + cropBox.width - displayX,
          y: cropBox.y + displayY
        };
      case 270:
        return {
          x: cropBox.x + cropBox.width - displayY,
          y: cropBox.y + cropBox.height - displayX
        };
      default:
        return {
          x: cropBox.x + displayX,
          y: cropBox.y + cropBox.height - displayY
        };
    }
  };

  const browserRectToPdf = (rect, geometry) => {
    // pdf-lib anchors rotated content at its lower-left corner. In browser
    // space that is the rectangle's bottom-left point.
    const anchor = browserPointToPdf(rect.x, rect.y + rect.height, geometry);
    const corners = [
      browserPointToPdf(rect.x, rect.y, geometry),
      browserPointToPdf(rect.x + rect.width, rect.y, geometry),
      browserPointToPdf(rect.x, rect.y + rect.height, geometry),
      browserPointToPdf(rect.x + rect.width, rect.y + rect.height, geometry)
    ];

    return {
      x: anchor.x,
      y: anchor.y,
      width: rect.width / geometry.unitScale,
      height: rect.height / geometry.unitScale,
      rotate: degrees(geometry.rotation),
      annotationRect: [
        Math.min(...corners.map(point => point.x)),
        Math.min(...corners.map(point => point.y)),
        Math.max(...corners.map(point => point.x)),
        Math.max(...corners.map(point => point.y))
      ]
    };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        await loadPdfPages(acceptedFiles[0]);
      }
    }
  });

  const loadPdfPages = async (file) => {
    setIsLoading(true);
    try {
      const fileData = await file.arrayBuffer();
      const uint8Array = new Uint8Array(fileData);
      
      const pdf = await getDocument({
        data: uint8Array,
        useWorkerFetch: false,
        isEvalSupported: false
      }).promise;
      
      const pagePromises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        pagePromises.push(renderPageThumbnail(pdf, i));
      }

      const renderedPages = await Promise.all(pagePromises);
      
      setPages(renderedPages.map((pageData, index) => ({
        number: index + 1,
        thumbnail: pageData.thumbnail,
        backgroundCanvas: pageData.backgroundCanvas,
        // FIXED: Store actual PDF dimensions
        pdfDimensions: pageData.pdfDimensions,
        canvasDimensions: pageData.canvasDimensions,
        viewportTransform: pageData.viewportTransform,
        rotation: pageData.rotation
      })));

      // Initialize media elements
      const initialMedia = {};
      for (let i = 1; i <= pdf.numPages; i++) {
        initialMedia[i] = [];
      }
      setMediaElements(initialMedia);

      await pdf.destroy();

    } catch (error) {
      toast.error('Error loading PDF: ' + error.message);
      console.error('PDF loading error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPageThumbnail = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    
    // Get actual PDF page dimensions
    const pdfViewport = page.getViewport({ scale: 1 });
    
    // Create thumbnail
    const thumbScale = 0.3;
    const thumbViewport = page.getViewport({ scale: thumbScale });
    
    const thumbnailCanvas = document.createElement('canvas');
    thumbnailCanvas.width = thumbViewport.width;
    thumbnailCanvas.height = thumbViewport.height;
    
    await page.render({
      canvasContext: thumbnailCanvas.getContext('2d'),
      viewport: thumbViewport
    }).promise;

    // Create working canvas
    const workingScale = 1.0; // Use 1:1 scale for accuracy
    const workingViewport = page.getViewport({ scale: workingScale });
    
    const backgroundCanvas = document.createElement('canvas');
    backgroundCanvas.width = workingViewport.width;
    backgroundCanvas.height = workingViewport.height;
    
    await page.render({
      canvasContext: backgroundCanvas.getContext('2d'),
      viewport: workingViewport
    }).promise;
    
    return {
      thumbnail: thumbnailCanvas.toDataURL(),
      backgroundCanvas: backgroundCanvas.toDataURL(),
      // FIXED: Store both PDF and canvas dimensions
      pdfDimensions: {
        width: pdfViewport.width,
        height: pdfViewport.height
      },
      canvasDimensions: {
        width: workingViewport.width,
        height: workingViewport.height
      },
      viewportTransform: [...workingViewport.transform],
      rotation: workingViewport.rotation
    };
  };

  const selectPage = async (pageNumber) => {
    if (snipCleanupRef.current) {
      snipCleanupRef.current();
      snipCleanupRef.current = null;
    }
    setCurrentPage(pageNumber);
    const pageData = pages.find(p => p.number === pageNumber);
    if (pageData && workingAreaRef.current) {
      await renderWorkingArea(pageData);
    }
  };

  const renderWorkingArea = async (pageData) => {
    if (!workingAreaRef.current) return;

    const workingArea = workingAreaRef.current;
    workingArea.innerHTML = '';

    const pageWrapper = document.createElement('div');
    pageWrapper.style.position = 'relative';
    pageWrapper.style.margin = '0 auto';
    pageWrapper.style.maxWidth = '100%';
    pageWrapper.style.overflow = 'hidden';
    pageWrapper.style.border = '1px solid #e5e7eb';
    pageWrapper.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
    pageWrapper.style.backgroundColor = '#ffffff';
    pageWrapper.className = 'pdf-page-wrapper';

    const backgroundImg = document.createElement('img');
    backgroundImg.src = pageData.backgroundCanvas;
    backgroundImg.className = 'pdf-page-background';
    backgroundImg.style.display = 'block';
    backgroundImg.style.width = '100%';
    backgroundImg.style.height = '100%';
    backgroundImg.style.pointerEvents = 'none';
    
    await new Promise((resolve) => {
      backgroundImg.onload = () => {
        pageWrapper.style.width = backgroundImg.naturalWidth + 'px';
        pageWrapper.style.aspectRatio = `${backgroundImg.naturalWidth} / ${backgroundImg.naturalHeight}`;
        resolve();
      };
      backgroundImg.onerror = resolve;
    });

    pageWrapper.appendChild(backgroundImg);
    // The wrapper must be in the document before overlay dimensions are
    // measured; its CSS max-width may make it smaller than the source canvas.
    workingArea.appendChild(pageWrapper);

    // Render existing elements
    if (mediaElements[pageData.number]) {
      mediaElements[pageData.number].forEach(element => {
        renderMediaElement(element, pageWrapper, pageData);
      });
    }
    
  };

  const renderMediaElement = (element, container, pageData) => {
    const el = document.createElement('div');
    el.className = 'media-element';
    el.style.position = 'absolute';
    
    // FIXED: Proper coordinate conversion from PDF to canvas display
    const scaleX = container.clientWidth / pageData.pdfDimensions.width;
    const scaleY = container.clientHeight / pageData.pdfDimensions.height;
    
    // Convert from PDF coordinates (stored in element) to canvas display coordinates
    const displayX = element.pdfX * scaleX;
    const displayY = element.pdfY * scaleY;
    const displayWidth = element.pdfWidth * scaleX;
    const displayHeight = element.pdfHeight * scaleY;
    
    el.style.left = displayX + 'px';
    el.style.top = displayY + 'px';
    el.style.width = displayWidth + 'px';
    el.style.height = displayHeight + 'px';
    el.style.cursor = 'move';
    el.style.border = '2px solid transparent';
    el.style.boxSizing = 'border-box';
    el.style.zIndex = '10';
    el.setAttribute('data-element-id', element.id);

    switch (element.type) {
      case 'image':
        const img = document.createElement('img');
        img.src = element.src;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        img.style.pointerEvents = 'none';
        el.appendChild(img);
        break;
        
      case 'button':
        el.style.backgroundColor = element.hasBackground === false
          ? 'transparent'
          : (element.backgroundColor || '#232751');
        el.style.color = element.color || '#ffffff';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.borderRadius = '4px';
        el.style.fontSize = `${Math.max(8, (element.fontSize || 16) * scaleY)}px`;
        el.style.fontWeight = '500';
        el.style.userSelect = 'none';
        el.style.textAlign = 'center';
        el.style.padding = '2px';
        el.textContent = element.text;
        break;
        
      case 'note':
        el.style.backgroundColor = element.backgroundColor || '#facc15';
        el.style.padding = '4px';
        el.style.fontSize = Math.max(8, displayHeight * 0.15) + 'px';
        el.style.borderRadius = '4px';
        el.style.border = '1px solid #eab308';
        el.style.whiteSpace = 'pre-wrap';
        el.style.overflow = 'hidden';
        el.style.userSelect = 'none';
        el.style.lineHeight = '1.2';
        el.textContent = element.text;
        break;

      case 'text':
        el.style.color = element.color || '#111827';
        el.style.backgroundColor = 'transparent';
        el.style.fontSize = `${Math.max(8, (element.fontSize || 18) * scaleY)}px`;
        el.style.whiteSpace = 'pre-wrap';
        el.style.overflow = 'hidden';
        el.style.userSelect = 'none';
        el.style.lineHeight = '1.2';
        el.style.padding = '2px';
        el.textContent = element.text;
        break;
    }

    makeElementInteractive(el, element, container, pageData);

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      document.querySelectorAll('.media-element').forEach(elem => {
        elem.style.border = '2px solid transparent';
      });
      el.style.border = '2px solid #232751';
      setSelectedElement(element.id);
    });

    el.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (element.type === 'button') {
        setEditingElement(element);
        setShowLinkModal(true);
      } else if (element.type === 'note') {
        setEditingElement(element);
        setShowNoteModal(true);
      } else if (element.type === 'text') {
        setEditingElement(element);
        setShowTextModal(true);
      }
    });

    container.appendChild(el);
  };

  const makeElementInteractive = (el, element, container, pageData) => {
    interact(el)
      .draggable({
        listeners: {
          move: (event) => {
            const target = event.target;
            const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
            
            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
            
            // FIXED: Calculate PDF coordinates from the actual final position
            const rect = target.getBoundingClientRect();
            const pageRect = container.querySelector('.pdf-page-background').getBoundingClientRect();
            
            // Get the actual position relative to container
            const actualDisplayX = rect.left - pageRect.left;
            const actualDisplayY = rect.top - pageRect.top;
            
            // Convert to PDF coordinates
            const scaleX = pageRect.width / pageData.pdfDimensions.width;
            const scaleY = pageRect.height / pageData.pdfDimensions.height;
            
            const newPdfX = actualDisplayX / scaleX;
            const newPdfY = actualDisplayY / scaleY;
            
            updateElementPosition(element.id, newPdfX, newPdfY);
          }
        },
        modifiers: [
          interact.modifiers.restrictRect({
            restriction: 'parent',
            endOnly: true
          })
        ]
      })
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },
        listeners: {
          move: (event) => {
            const target = event.target;
            let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.deltaRect.left;
            let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.deltaRect.top;
            
            target.style.width = event.rect.width + 'px';
            target.style.height = event.rect.height + 'px';
            target.style.transform = `translate(${x}px, ${y}px)`;
            
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
            
            // FIXED: Calculate PDF dimensions from actual size
            const pageRect = container.querySelector('.pdf-page-background').getBoundingClientRect();
            const scaleX = pageRect.width / pageData.pdfDimensions.width;
            const scaleY = pageRect.height / pageData.pdfDimensions.height;
            
            const newPdfWidth = event.rect.width / scaleX;
            const newPdfHeight = event.rect.height / scaleY;
            
            updateElementSize(element.id, newPdfWidth, newPdfHeight);
            
            // Also update position if element was moved during resize
            const rect = target.getBoundingClientRect();
            
            const actualDisplayX = rect.left - pageRect.left;
            const actualDisplayY = rect.top - pageRect.top;
            
            const newPdfX = actualDisplayX / scaleX;
            const newPdfY = actualDisplayY / scaleY;
            
            updateElementPosition(element.id, newPdfX, newPdfY);
          }
        },
        modifiers: [
          interact.modifiers.restrictSize({
            min: { width: 20, height: 20 }
          })
        ]
      });
  };

  const updateElementPosition = (elementId, newPdfX, newPdfY) => {
    if (!currentPage) return;
    
    setMediaElements(prev => ({
      ...prev,
      [currentPage]: prev[currentPage].map(el => 
        el.id === elementId ? { ...el, pdfX: newPdfX, pdfY: newPdfY } : el
      )
    }));
  };

  const updateElementSize = (elementId, newPdfWidth, newPdfHeight) => {
    if (!currentPage) return;
    
    setMediaElements(prev => ({
      ...prev,
      [currentPage]: prev[currentPage].map(el => 
        el.id === elementId ? { ...el, pdfWidth: newPdfWidth, pdfHeight: newPdfHeight } : el
      )
    }));
  };

  const addImage = () => {
    if (!currentPage) {
      toast.error('Please select a page first');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', async (e) => {
      if (e.target.files[0]) {
        const reader = new FileReader();
        reader.onload = async function() {
          const pageData = pages.find(p => p.number === currentPage);
          // FIXED: Store coordinates in PDF units
          const newElement = {
            id: Date.now(),
            type: 'image',
            pdfX: pageData.pdfDimensions.width * 0.1,
            pdfY: pageData.pdfDimensions.height * 0.1,
            pdfWidth: pageData.pdfDimensions.width * 0.3,
            pdfHeight: pageData.pdfDimensions.height * 0.2,
            src: reader.result
          };
          
          setMediaElements(prev => ({
            ...prev,
            [currentPage]: [...(prev[currentPage] || []), newElement]
          }));
          
          setTimeout(() => renderWorkingArea(pageData), 100);
        };
        reader.readAsDataURL(e.target.files[0]);
      }
    });
    input.click();
  };

  const stopSnipping = () => {
    if (snipCleanupRef.current) {
      snipCleanupRef.current();
      snipCleanupRef.current = null;
    }
    setIsSnipping(false);
  };

  const addSnip = () => {
    if (!currentPage) {
      toast.error('Please select a page first');
      return;
    }

    if (isSnipping) {
      stopSnipping();
      return;
    }

    const pageData = pages.find(page => page.number === currentPage);
    const pageWrapper = workingAreaRef.current?.querySelector('.pdf-page-wrapper');
    const backgroundImg = pageWrapper?.querySelector('.pdf-page-background');
    if (!pageData || !pageWrapper || !backgroundImg) {
      toast.error('The selected page is not ready yet');
      return;
    }

    const pageRect = backgroundImg.getBoundingClientRect();
    const wrapperRect = pageWrapper.getBoundingClientRect();
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.left = `${pageRect.left - wrapperRect.left}px`;
    overlay.style.top = `${pageRect.top - wrapperRect.top}px`;
    overlay.style.width = `${pageRect.width}px`;
    overlay.style.height = `${pageRect.height}px`;
    overlay.style.cursor = 'crosshair';
    overlay.style.zIndex = '50';
    overlay.style.touchAction = 'none';
    overlay.style.backgroundColor = 'rgba(37, 99, 235, 0.03)';

    const selection = document.createElement('div');
    selection.style.position = 'absolute';
    selection.style.display = 'none';
    selection.style.border = '2px dashed #232751';
    selection.style.backgroundColor = 'rgba(37, 99, 235, 0.16)';
    selection.style.pointerEvents = 'none';
    selection.style.boxSizing = 'border-box';
    overlay.appendChild(selection);
    pageWrapper.appendChild(overlay);

    let startX = 0;
    let startY = 0;
    let dragging = false;

    const clampToPage = (value, maximum) => Math.max(0, Math.min(value, maximum));
    const getPoint = event => {
      const rect = overlay.getBoundingClientRect();
      return {
        x: clampToPage(event.clientX - rect.left, rect.width),
        y: clampToPage(event.clientY - rect.top, rect.height)
      };
    };

    const cleanup = () => {
      overlay.remove();
      document.removeEventListener('keydown', handleKeyDown);
      setIsSnipping(false);
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        cleanup();
        snipCleanupRef.current = null;
      }
    };

    overlay.addEventListener('pointerdown', event => {
      const point = getPoint(event);
      startX = point.x;
      startY = point.y;
      dragging = true;
      selection.style.display = 'block';
      selection.style.left = `${startX}px`;
      selection.style.top = `${startY}px`;
      selection.style.width = '0px';
      selection.style.height = '0px';
      overlay.setPointerCapture(event.pointerId);
      event.preventDefault();
    });

    overlay.addEventListener('pointermove', event => {
      if (!dragging) return;
      const point = getPoint(event);
      const x = Math.min(startX, point.x);
      const y = Math.min(startY, point.y);
      const width = Math.abs(point.x - startX);
      const height = Math.abs(point.y - startY);
      selection.style.left = `${x}px`;
      selection.style.top = `${y}px`;
      selection.style.width = `${width}px`;
      selection.style.height = `${height}px`;
    });

    overlay.addEventListener('pointerup', event => {
      if (!dragging) return;
      dragging = false;
      const point = getPoint(event);
      const x = Math.min(startX, point.x);
      const y = Math.min(startY, point.y);
      const width = Math.abs(point.x - startX);
      const height = Math.abs(point.y - startY);

      if (width < 5 || height < 5) {
        selection.style.display = 'none';
        return;
      }

      const sourceScaleX = backgroundImg.naturalWidth / pageRect.width;
      const sourceScaleY = backgroundImg.naturalHeight / pageRect.height;
      const snipCanvas = document.createElement('canvas');
      snipCanvas.width = Math.max(1, Math.round(width * sourceScaleX));
      snipCanvas.height = Math.max(1, Math.round(height * sourceScaleY));
      snipCanvas.getContext('2d').drawImage(
        backgroundImg,
        x * sourceScaleX,
        y * sourceScaleY,
        width * sourceScaleX,
        height * sourceScaleY,
        0,
        0,
        snipCanvas.width,
        snipCanvas.height
      );

      const displayScaleX = pageRect.width / pageData.pdfDimensions.width;
      const displayScaleY = pageRect.height / pageData.pdfDimensions.height;
      const newElement = {
        id: Date.now(),
        type: 'image',
        pdfX: x / displayScaleX,
        pdfY: y / displayScaleY,
        pdfWidth: width / displayScaleX,
        pdfHeight: height / displayScaleY,
        src: snipCanvas.toDataURL('image/png'),
        isSnip: true
      };

      setMediaElements(prev => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), newElement]
      }));
      cleanup();
      snipCleanupRef.current = null;
      renderMediaElement(newElement, pageWrapper, pageData);
      toast.success('Snip added. Drag it to reposition.');
    });

    overlay.addEventListener('pointercancel', () => {
      cleanup();
      snipCleanupRef.current = null;
    });
    document.addEventListener('keydown', handleKeyDown);
    snipCleanupRef.current = cleanup;
    setIsSnipping(true);
    toast('Drag over the page to create a snip. Press Esc to cancel.');
  };

  const addButton = () => {
    if (!currentPage) {
      toast.error('Please select a page first');
      return;
    }
    setEditingElement(null);
    setShowLinkModal(true);
  };

  const addNote = () => {
    if (!currentPage) {
      toast.error('Please select a page first');
      return;
    }
    setEditingElement(null);
    setShowNoteModal(true);
  };

  const addText = () => {
    if (!currentPage) {
      toast.error('Please select a page first');
      return;
    }
    setEditingElement(null);
    setShowTextModal(true);
  };

  const handleLinkSave = async (linkData) => {
    const pageData = pages.find(p => p.number === currentPage);
    
    if (editingElement) {
      setMediaElements(prev => ({
        ...prev,
        [currentPage]: prev[currentPage].map(el => 
          el.id === editingElement.id 
            ? { ...el, ...linkData }
            : el
        )
      }));
    } else {
      // FIXED: Store coordinates in PDF units
      const newElement = {
        id: Date.now(),
        type: 'button',
        pdfX: pageData.pdfDimensions.width * 0.1,
        pdfY: pageData.pdfDimensions.height * 0.1,
        pdfWidth: pageData.pdfDimensions.width * 0.25,
        pdfHeight: pageData.pdfDimensions.height * 0.08,
        ...linkData
      };
      
      setMediaElements(prev => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), newElement]
      }));
    }
    
    setTimeout(() => renderWorkingArea(pageData), 100);
    setEditingElement(null);
  };

  const handleNoteSave = async (noteData) => {
    const pageData = pages.find(p => p.number === currentPage);
    
    if (editingElement) {
      setMediaElements(prev => ({
        ...prev,
        [currentPage]: prev[currentPage].map(el => 
          el.id === editingElement.id 
            ? { ...el, ...noteData }
            : el
        )
      }));
    } else {
      // FIXED: Store coordinates in PDF units
      const newElement = {
        id: Date.now(),
        type: 'note',
        pdfX: pageData.pdfDimensions.width * 0.1,
        pdfY: pageData.pdfDimensions.height * 0.1,
        pdfWidth: pageData.pdfDimensions.width * 0.3,
        pdfHeight: pageData.pdfDimensions.height * 0.15,
        ...noteData
      };
      
      setMediaElements(prev => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), newElement]
      }));
    }
    
    setTimeout(() => renderWorkingArea(pageData), 100);
    setEditingElement(null);
  };

  const handleTextSave = async (textData) => {
    const pageData = pages.find(p => p.number === currentPage);

    if (editingElement) {
      setMediaElements(prev => ({
        ...prev,
        [currentPage]: prev[currentPage].map(el =>
          el.id === editingElement.id
            ? { ...el, ...textData }
            : el
        )
      }));
    } else {
      const newElement = {
        id: Date.now(),
        type: 'text',
        pdfX: pageData.pdfDimensions.width * 0.1,
        pdfY: pageData.pdfDimensions.height * 0.1,
        pdfWidth: pageData.pdfDimensions.width * 0.35,
        pdfHeight: pageData.pdfDimensions.height * 0.12,
        ...textData
      };

      setMediaElements(prev => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), newElement]
      }));
    }

    setTimeout(() => renderWorkingArea(pageData), 100);
    setEditingElement(null);
  };

  const deleteSelected = () => {
    if (!selectedElement || !currentPage) return;
    
    setMediaElements(prev => ({
      ...prev,
      [currentPage]: prev[currentPage].filter(el => el.id !== selectedElement)
    }));
    
    setSelectedElement(null);
    
    const pageData = pages.find(p => p.number === currentPage);
    if (pageData) {
      setTimeout(() => renderWorkingArea(pageData), 100);
    }
  };

  // FIXED: Simplified saveMediaPDF function using PDF coordinates directly
  const saveMediaPDF = () => {
    if (!pdfFile) {
      toast.error('No PDF loaded');
      return;
    }

    const hasMediaElements = Object.values(mediaElements).some(pageElements => pageElements.length > 0);
    if (!hasMediaElements) {
      toast.error('Please add some media elements before saving');
      return;
    }

    requireAuth(async () => {
    setIsLoading(true);
    try {
      console.log('Starting PDF save process...');
      
      const freshFileData = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(freshFileData);
      const pdfPages = pdfDoc.getPages();

      for (let pageNum = 1; pageNum <= pages.length; pageNum++) {
        const pageElements = mediaElements[pageNum] || [];
        if (pageElements.length === 0) continue;

        const page = pdfPages[pageNum - 1];
        const pageData = pages.find(pageInfo => pageInfo.number === pageNum);
        const geometry = getExportGeometry(page, pageData);
        const annotations = [];

        console.log(`Processing page ${pageNum} with ${pageElements.length} elements`);
        console.log(`Page rotation: ${geometry.rotation} degrees`);

        for (const element of pageElements) {
          try {
            const browserRect = {
              x: element.pdfX * geometry.scaleX,
              y: element.pdfY * geometry.scaleY,
              width: element.pdfWidth * geometry.scaleX,
              height: element.pdfHeight * geometry.scaleY
            };
            const {
              x, y, width, height, rotate, annotationRect
            } = browserRectToPdf(browserRect, geometry);

            console.log(`Adding ${element.type} at (${x.toFixed(1)}, ${y.toFixed(1)}) size ${width.toFixed(1)}x${height.toFixed(1)}`);

            switch (element.type) {
              case 'image':
                try {
                  if (element.src && element.src.startsWith('data:')) {
                    const base64Data = element.src.split(',')[1];
                    const binaryString = window.atob(base64Data);
                    const imageBytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                      imageBytes[i] = binaryString.charCodeAt(i);
                    }
                    
                    let embeddedImage;
                    if (element.src.includes('data:image/png')) {
                      embeddedImage = await pdfDoc.embedPng(imageBytes);
                    } else {
                      embeddedImage = await pdfDoc.embedJpg(imageBytes);
                    }
                    
                    // Match the editor's object-fit: contain instead of
                    // stretching the image to fill its resize handles.
                    const imageScale = Math.min(
                      browserRect.width / embeddedImage.width,
                      browserRect.height / embeddedImage.height
                    );
                    const containedRect = {
                      width: embeddedImage.width * imageScale,
                      height: embeddedImage.height * imageScale,
                      x: browserRect.x + (browserRect.width - (embeddedImage.width * imageScale)) / 2,
                      y: browserRect.y + (browserRect.height - (embeddedImage.height * imageScale)) / 2
                    };
                    const imageRect = browserRectToPdf(containedRect, geometry);

                    page.drawImage(embeddedImage, {
                      x: imageRect.x,
                      y: imageRect.y,
                      width: imageRect.width,
                      height: imageRect.height,
                      rotate: imageRect.rotate
                    });
                    console.log('Image embedded successfully');
                  }
                } catch (imgError) {
                  console.error('Error embedding image:', imgError);
                }
                break;

              case 'button':
                if (element.hasBackground !== false) {
                  page.drawRectangle({
                    x, y, width, height,
                    rotate,
                    color: hexToRgb(element.backgroundColor || '#232751'),
                  });
                }

                const fontSize = Math.max(8, Math.min(element.fontSize || 16, height * 0.8));
                const textWidth = fontSize * element.text.length * 0.6;
                const textDisplayX = browserRect.x + Math.max(4, (width - textWidth) / 2);
                const textDisplayY = browserRect.y + ((height + fontSize) / 2);
                const textPoint = browserPointToPdf(textDisplayX, textDisplayY, geometry);
                
                page.drawText(element.text, {
                  x: textPoint.x,
                  y: textPoint.y,
                  size: fontSize,
                  color: hexToRgb(element.color || '#ffffff'),
                  rotate,
                });
                
                annotations.push(pdfDoc.context.obj({
                  Type: PDFName.of('Annot'),
                  Subtype: PDFName.of('Link'),
                  Rect: annotationRect,
                  Border: [0, 0, 0],
                  A: {
                    Type: PDFName.of('Action'),
                    S: PDFName.of('URI'),
                    URI: PDFString.of(element.url)
                  }
                }));
                console.log('Button with link added successfully');
                break;

              case 'note':
                page.drawRectangle({
                  x, y, width, height,
                  rotate,
                  color: hexToRgb(element.backgroundColor || '#facc15'),
                  borderWidth: 1,
                  borderColor: hexToRgb('#eab308'),
                });
                
                const noteFontSize = Math.min(height * 0.12, width * 0.05, 12);
                const lines = wrapText(element.text, width - 16, noteFontSize);
                
                lines.forEach((line, index) => {
                  const lineDisplayY = browserRect.y + (noteFontSize + 4) + (index * (noteFontSize + 2));
                  if (lineDisplayY < browserRect.y + height - 4) {
                    const linePoint = browserPointToPdf(browserRect.x + 8, lineDisplayY, geometry);
                    page.drawText(line, {
                      x: linePoint.x,
                      y: linePoint.y,
                      size: noteFontSize,
                      color: rgb(0, 0, 0),
                      rotate,
                    });
                  }
                });
                console.log('Note added successfully');
                break;

              case 'text':
                const textFontSize = Math.max(8, Math.min(element.fontSize || 18, height * 0.8));
                const textLines = wrapText(element.text, width - 4, textFontSize);

                textLines.forEach((line, index) => {
                  const lineDisplayY = browserRect.y + textFontSize + (index * (textFontSize + 2));
                  if (lineDisplayY <= browserRect.y + height) {
                    const linePoint = browserPointToPdf(browserRect.x + 2, lineDisplayY, geometry);
                    page.drawText(line, {
                      x: linePoint.x,
                      y: linePoint.y,
                      size: textFontSize,
                      color: hexToRgb(element.color || '#111827'),
                      rotate,
                    });
                  }
                });
                console.log('Text added successfully');
                break;
            }
          } catch (elementError) {
            console.error(`Error adding element to page ${pageNum}:`, elementError);
          }
        }

        if (annotations.length > 0) {
          page.node.set(PDFName.of('Annots'), pdfDoc.context.obj(annotations));
          console.log(`Added ${annotations.length} annotations to page ${pageNum}`);
        }
      }

      console.log('Saving PDF...');
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `media_${pdfFile.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      

      setProcessedBlob(blob);
      setShowPreviewSave(true);
      toast.success('Media PDF created successfully!');
      
    } catch (error) {
      console.error('Error saving media PDF:', error);
      toast.error('Failed to save media PDF: ' + error.message);
    } finally {
      setIsLoading(false);
    }
    });
  };

  const resetForm = () => {
    if (snipCleanupRef.current) {
      snipCleanupRef.current();
      snipCleanupRef.current = null;
    }
    setPdfFile(null);
    setPages([]);
    setCurrentPage(null);
    setMediaElements({});
    setSelectedElement(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (workingAreaRef.current && !e.target.closest('.media-element')) {
        document.querySelectorAll('.media-element').forEach(el => {
          el.style.border = '2px solid transparent';
        });
        setSelectedElement(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Add Media to PDF</h2>
      
      {!pdfFile && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-[#232751] bg-[#232751]/5' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-sm font-medium text-gray-600 mb-2">
            {isDragActive ? 'Drop PDF here...' : 'Click to select or drag PDF here'}
          </p>
          <p className="text-xs text-gray-500">
            Upload a PDF to add interactive media elements
          </p>
        </div>
      )}

      {pages.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700">Select a page to edit:</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {pages.map((page) => (
              <div
                key={page.number}
                onClick={() => selectPage(page.number)}
                className={`
                  relative cursor-pointer rounded-lg overflow-hidden transition-all border-2
                  ${currentPage === page.number 
                    ? 'border-[#232751] shadow-lg transform scale-105' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }
                `}
              >
                <img
                  src={page.thumbnail}
                  alt={`Page ${page.number}`}
                  className="w-full h-auto"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-center py-1 text-sm">
                  Page {page.number}
                </div>
                {mediaElements[page.number]?.length > 0 && (
                  <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                    {mediaElements[page.number].length}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {currentPage && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-lg font-medium text-gray-700">
              Editing Page {currentPage} ({mediaElements[currentPage]?.length || 0} elements)
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={addImage}
                className="px-3 py-2 bg-[#232751]/10 text-[#232751] rounded-lg hover:bg-[#232751]/20 transition-colors flex items-center text-sm"
              >
                <Image className="w-4 h-4 mr-1" />
                Image
              </button>
              <button
                onClick={addSnip}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center text-sm ${
                  isSnipping
                    ? 'bg-[#232751] text-white hover:bg-[#1b1f43]'
                    : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                }`}
              >
                <Scissors className="w-4 h-4 mr-1" />
                {isSnipping ? 'Cancel Snip' : 'Snip'}
              </button>
              <button
                onClick={addButton}
                className="px-3 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors flex items-center text-sm"
              >
                <Link className="w-4 h-4 mr-1" />
                Link
              </button>
              <button
                onClick={addNote}
                className="px-3 py-2 bg-yellow-100 text-yellow-600 rounded-lg hover:bg-yellow-200 transition-colors flex items-center text-sm"
              >
                <StickyNote className="w-4 h-4 mr-1" />
                Note
              </button>
              <button
                onClick={addText}
                className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors flex items-center text-sm"
              >
                <Edit className="w-4 h-4 mr-1" />
                Text
              </button>
              {selectedElement && (
                <button
                  onClick={deleteSelected}
                  className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
              )}
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-96">
            <div
              ref={workingAreaRef}
              className="w-full flex justify-center items-start"
              style={{ minHeight: '400px' }}
            />
          </div>
          
          <div className="bg-[#232751]/5 border border-[#232751]/20 rounded-lg p-4">
            <h4 className="font-medium text-[#232751] mb-2">Instructions:</h4>
            <ul className="text-sm text-[#232751]/80 space-y-1">
              <li>Use Snip, then drag over the page to create a movable clipping</li>
              <li>• Click and drag elements to reposition them</li>
              <li>• Drag the corners or edges to resize elements</li>
              <li>• Click an element to select it, then use Delete button to remove</li>
              <li>• Double-click elements to edit their properties</li>
              <li>• Links will be clickable in the final PDF</li>
              <li>• Use "Save Media PDF" to download your enhanced PDF</li>
            </ul>
          </div>
        </div>
      )}

      {pdfFile && (
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={saveMediaPDF}
            disabled={isLoading || Object.values(mediaElements).every(arr => arr.length === 0)}
            className="flex-1 bg-[#232751] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#1b1f43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Save className="w-5 h-5 mr-2" />
            {isLoading ? 'Saving...' : 'Save Media PDF'}
          </button>
          
          <button
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
        </div>
      )}

      {AuthModal}

      {/* Modals */}
      <LinkModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setEditingElement(null);
        }}
        onSave={handleLinkSave}
        initialData={editingElement || {}}
      />

      <NoteModal
        isOpen={showNoteModal}
        onClose={() => {
          setShowNoteModal(false);
          setEditingElement(null);
        }}
        onSave={handleNoteSave}
        initialData={editingElement || {}}
      />

      <TextModal
        isOpen={showTextModal}
        onClose={() => {
          setShowTextModal(false);
          setEditingElement(null);
        }}
        onSave={handleTextSave}
        initialData={editingElement || {}}
      />
    </div>
  );
};

export default AddMediaPDF;
